import React, { useState } from 'react';
import {
  SlDialog,
  SlDialogHeader,
  SlDialogCloseButton,
  SlDialogTitle,
  SlDialogContent,
  SlDialogFooter,
  SlDialogProps
} from 'src/components/BeaconV2Components/SlDialog';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SearchTextField } from 'src/components/BeaconV2Components/SearchInput/SearchInputBar';
import { Box, Divider, SxProps } from '@mui/material';
import { topicSettingsServerProxy } from './serverProxy';
import { useQuery } from '@tanstack/react-query';
import { Topic } from '../types';
import ScrollableContainer from 'src/components/BeaconV2Components/ScrollableContainer';
import { SlCheckboxContainer } from 'src/components/BeaconV2Components/SlCheckbox';
import { SlShowMore } from 'src/components/BeaconV2Components/SlShowMore/SlShowMore';
import _capitalize from 'lodash/capitalize';
import { UserMetaData, userMetadataServerProxy } from 'src/serverProxy/userMetadataServerProxy';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton';
import { useAppSelector } from 'src/utils/Hooks';
import {
  useAddTopicGroupStoreActions,
  useAddTopicGroupStoreState
} from 'src/zustandStores/BeaconPro/Topics/TopicGroups/useAddTopicGroupStore';
import CheckboxItem from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/CheckboxItem';
import { useStacklineTheme } from '@stackline/ui';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';

interface TopicWithOwnership extends Topic {
  ownership: string;
  owner: UserMetaData;
}

function TopicItemGrid({ sx, children }: { sx?: SxProps; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        ...{
          display: 'grid',
          gridTemplateColumns: '358px 50px 1fr',
          columnGap: '56px',
          rowGap: '16px',
          alignItems: 'center'
        },
        ...sx
      }}
    >
      {children}
    </Box>
  );
}

interface EditTopicGroupModalProps extends SlDialogProps {
  title: string;
  footer: (args: { selectedTopicIds: string[]; handleClear: () => void }) => React.ReactNode;
}
/**
 * Adds topics to a topic group
 */
export function EditTopicGroupModal({ title, footer, onClose, open, ...rest }: EditTopicGroupModalProps) {
  const [selectedTopicType, setSelectedTopicType] = useState('all');
  const [search, setSearch] = useState('');
  const { selectedTopicIds } = useAddTopicGroupStoreState().topicGroupState;
  const { setTopicGroupState } = useAddTopicGroupStoreActions();
  const userId = useAppSelector((state) => state.user.session.userId);
  const theme = useStacklineTheme();

  const { data: topics, isLoading } = useQuery({
    enabled: open,
    queryKey: [TOPIC_QUERY_KEYS.all, 'GetTopicsForGroup'],
    queryFn: async () => {
      const [customTopics, ownedTopics] = await Promise.all([
        topicSettingsServerProxy.getCustomTopics(),
        topicSettingsServerProxy.getOwnedTopics()
      ]);

      const userIds = [
        ...new Set([
          ...customTopics.map((topic) => topic.ownedByUserId),
          ...ownedTopics.map((topic) => topic.ownedByUserId)
        ])
      ];

      async function getUserProfiles() {
        if (userIds.length < 1) {
          return [];
        }
        return userMetadataServerProxy.getUserProfiles(userIds);
      }

      const users = await getUserProfiles();

      const response: TopicWithOwnership[] = [
        ...customTopics
          .map((topic) => ({
            ...topic,
            ownership: 'custom',
            owner: users[topic.ownedByUserId]
          }))
          .filter((topic) => topic.ownedByUserId !== userId),
        ...ownedTopics.map((topic) => ({
          ...topic,
          ownership: 'owned',
          owner: users[topic.ownedByUserId]
        }))
      ];

      return response;
    }
  });

  const handleClear = () => {
    setSearch('');
    setTopicGroupState(({ setTopicIds }) => setTopicIds([]));
  };

  return (
    <SlDialog
      width="752px"
      height="641px"
      onClose={(event, reason) => {
        // eslint-disable-next-line no-unused-expressions
        onClose?.(event, reason);
        handleClear();
      }}
      open={open}
      {...rest}
    >
      <SlDialogHeader>
        <SlDialogTitle>{title}</SlDialogTitle>
        <SlDialogCloseButton onClick={onClose} />
      </SlDialogHeader>
      <SlDialogContent sx={{ overflow: 'hidden' }}>
        <Flex justifyContent="space-between" marginBottom="5px">
          <Text variant="subtitle1">Topics</Text>
          <SlDropdownMenu
            options={[
              {
                id: 'all',
                label: 'All'
              },
              {
                id: 'custom',
                label: 'All Custom'
              },
              {
                id: 'owned',
                label: 'All Owned'
              }
            ]}
            selectedId={selectedTopicType}
            onChange={(option) => setSelectedTopicType(option.id)}
          />
        </Flex>
        <SearchTextField placeholder="Search topics" value={search} onChange={(e) => setSearch(e.target.value)} />
        <TopicItemGrid
          sx={{
            marginTop: '32px',
            marginBottom: '16px'
          }}
        >
          <Text
            sx={{
              width: '394px',
              fontSize: '12px',
              fontWeight: 'bold',
              paddingLeft: '20px'
            }}
            transform="uppercase"
          >
            Topic
          </Text>
          <Text
            sx={{
              width: '106px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
            transform="uppercase"
          >
            Type
          </Text>
          <Text
            sx={{
              fontSize: '12px',
              fontWeight: 'bold'
            }}
            transform="uppercase"
          >
            Owner
          </Text>
        </TopicItemGrid>
        <Divider />
        {isLoading ? (
          <TopicItemGrid sx={{ marginTop: '16px' }}>
            {Array.from({ length: 7 }).map((_, i) => (
              <React.Fragment key={i}>
                <SlSkeleton width="100%" height={24} key={i} />
                <SlSkeleton width="100%" height={24} key={i} />
                <SlSkeleton width="100%" height={24} key={i} />
              </React.Fragment>
            ))}
          </TopicItemGrid>
        ) : (
            <ScrollableContainer
              sx={{
                height: '300px',
                marginTop: '16px'
              }}
            >
              <SlShowMore
                itemsPerChunk={20}
                items={topics
                  ?.filter((topic) => (selectedTopicType === 'all' ? true : topic.ownership === selectedTopicType))
                  .filter((topic) => topic.topicName.toLowerCase().includes(search.toLowerCase()))
                  .sort((a, b) => {
                    const aSelected = selectedTopicIds.includes(a.topicId);
                    const bSelected = selectedTopicIds.includes(b.topicId);
                    if (aSelected && !bSelected) {
                      return -1;
                    }
                    if (!aSelected && bSelected) {
                      return 1;
                    }
                    return 0;
                  })}
                renderItems={(items: TopicWithOwnership[]) => {
                  return (
                    <TopicItemGrid
                      sx={{
                        marginTop: '16px'
                      }}
                    >
                      {items.map((topic) => {
                        return (
                          <React.Fragment key={topic.topicId}>
                            <SlCheckboxContainer>
                              <CheckboxItem
                                theme={theme}
                                label={topic.topicName}
                                value={topic.topicId}
                                checked={selectedTopicIds.includes(topic.topicId)}
                                textSx={{ maxWidth: '350px' }}
                                onClick={(e) =>
                                  e.target.checked
                                    ? setTopicGroupState(({ setTopicIds }) =>
                                      setTopicIds([...selectedTopicIds, topic.topicId])
                                    )
                                    : setTopicGroupState(({ setTopicIds }) =>
                                      setTopicIds(selectedTopicIds.filter((id) => id !== topic.topicId))
                                    )
                                }
                              />
                            </SlCheckboxContainer>
                            <Text variant="body2">{_capitalize(topic.ownership)}</Text>
                            <Text variant="body2">{`${topic.owner.firstName} ${topic.owner.lastName}`}</Text>
                          </React.Fragment>
                        );
                      })}
                    </TopicItemGrid>
                  );
                }}
              />
            </ScrollableContainer>
          )}
      </SlDialogContent>
      <SlDialogFooter>{footer({ selectedTopicIds, handleClear })}</SlDialogFooter>
    </SlDialog>
  );
}
